import React, { useState, useEffect } from "react";
import {
  FaFileExcel,
  FaFilePdf,
  FaChevronDown,
  FaChevronUp,
  FaFileDownload,
} from "react-icons/fa";
import { toast } from "react-hot-toast";
import { useParams } from "react-router-dom";
import { formatDate, convertDateToWords } from "../../helper/dateFormat";
import Api from "../../services/api";
import { Menu, Transition } from "@headlessui/react";

const CandidateJobExporter = ({ data = null }) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({
    job_id: "",
    startDate: "",
    endDate: "",
    status: "",
  });
  const [uniqueJobs, setUniqueJobs] = useState([]);

  const { id: routeJobId } = useParams();

  useEffect(() => {
    if (data && data.length > 0) {
      // Extract unique jobs from provided data
      const jobsMap = {};
      data.forEach(item => {
        if (item.job && !jobsMap[item.job.id]) {
          jobsMap[item.job.id] = item.job;
        }
      });
      setUniqueJobs(Object.values(jobsMap));
      
      // Set first job as default if no routeJobId
      if (!routeJobId && Object.values(jobsMap).length > 0) {
        setFilters(prev => ({ ...prev, job_id: Object.values(jobsMap)[0].id }));
      }
    } else if (routeJobId) {
      // If no data provided but routeJobId exists
      setFilters(prev => ({ ...prev, job_id: routeJobId }));
    }
  }, [data, routeJobId]);

  const exportData = async (type) => {
    try {
      setLoading(true);

      const params = {
        job_id: filters.job_id || undefined,
        status: filters.status || undefined,
      };

      if (filters.startDate && filters.endDate) {
        params.date_from = formatDate(filters.startDate);
        params.date_to = formatDate(filters.endDate);
      }

      const response = await Api.getCandidateExport(type, params);

      if (response.data) {
        const contentDisposition = response.headers['content-disposition'];
        let filename = `candidate_jobs.${type === 'excel' ? 'xlsx' : 'pdf'}`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        toast.success(`Export ${type.toUpperCase()} generated successfully`);
      } else {
        throw new Error('No data received from server');
      }
    } catch (error) {
      toast.error(
        `Failed to export: ${error.response?.data?.message || error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
      <button
        className="w-full flex justify-between items-center px-4 py-3 text-left text-lg font-medium text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100 focus:outline-none transition-colors duration-150"
        onClick={() => setOpen(!open)}
      >
        <span className="flex items-center gap-2">
          <FaFileDownload className="text-indigo-600" />
          Export Candidate Jobs
        </span>
        {open ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      <Transition
        show={open}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="mt-4 space-y-4">
          {/* Job Selection Dropdown - Only shown when data is provided */}
          {data && uniqueJobs.length > 0 && (
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Job
              </label>
              <select
                className="w-full p-1.5 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 shadow-sm text-sm"
                value={filters.job_id}
                onChange={(e) =>
                  setFilters({ ...filters, job_id: e.target.value })
                }
              >
                {uniqueJobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-end gap-4 mt-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="w-full p-1.5 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 shadow-sm text-sm"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                className="w-full p-1.5 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 shadow-sm text-sm"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                className="w-full p-1.5 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 shadow-sm text-sm"
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
              >
                <option value="">All Statuses</option>
                <option value="0">Pending</option>
                <option value="1">Accepted</option>
                <option value="2">Rejected</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <Menu.Button
                  className={`inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={loading}
                >
                  <FaFileDownload className="mr-2" />
                  {loading ? "Exporting..." : "Export"}
                </Menu.Button>
              </div>
              <Transition
                as={React.Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute left-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => exportData("excel")}
                          className={`${
                            active
                              ? "bg-indigo-50 text-indigo-900"
                              : "text-gray-700"
                          } group flex w-full items-center px-4 py-2 text-sm`}
                        >
                          <FaFileExcel className="mr-2 text-green-600" />
                          Export as Excel
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => exportData("pdf")}
                          className={`${
                            active
                              ? "bg-indigo-50 text-indigo-900"
                              : "text-gray-700"
                          } group flex w-full items-center px-4 py-2 text-sm`}
                        >
                          <FaFilePdf className="mr-2 text-red-600" />
                          Export as PDF
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            <button
              onClick={() =>
                setFilters({
                  job_id: data && uniqueJobs.length > 0 ? uniqueJobs[0].id : routeJobId || "",
                  startDate: "",
                  endDate: "",
                  status: "",
                })
              }
              disabled={loading}
              className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default CandidateJobExporter;